import type React from 'react';
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, X, Home, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HeartsDisplay } from '@/components/HeartsDisplay';
import { CoachFeedback } from '@/components/CoachFeedback';
import { PlayDiagram } from '@/components/PlayDiagram';
import { DragDropQuestion } from '@/components/DragDropQuestion';
import { DiagramIdentifyQuestion } from '@/components/DiagramIdentifyQuestion';
import { TrueFalseQuestion } from '@/components/TrueFalseQuestion';
import { ScenarioQuestion } from '@/components/ScenarioQuestion';
import { SituationalPlayCall } from '@/components/SituationalPlayCall';
import { MatchingPairsQuestion } from '@/components/MatchingPairsQuestion';
import { CelebrationOverlay } from '@/components/CelebrationOverlay';
import { HeartsDepletedOverlay } from '@/components/HeartsDepletedOverlay';
import { shuffleOptionsWithCorrect } from '@/lib/shuffle';
import { GlossaryText } from '@/components/GlossaryOverlay';
import { GlossaryPanel } from '@/components/GlossaryPanel';
import { LessonContentSection } from '@/components/LessonContentSection';
import { usePreferredSport } from '@/hooks/usePreferredSport';
import { useAuth } from '@/hooks/useAuth';
import { lessonProgressKey } from '@/lib/deviceProgressState';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { useSportSkillLevel } from '@/hooks/useSportSkillLevel';
import { personalizeConcept, isPersonalizationActive, toneLabel } from '@/lib/lessonPersonalize';

import type { Lesson } from '@/data/lessons';
import type { QuestionResult } from '@/hooks/useProgress';


interface LessonViewProps {
  lesson: Lesson;
  hearts: number;
  completedLessons: string[];
  nextLesson?: Lesson | null;
  onComplete: (correct: number, total: number, xpReward: number, questionResults?: QuestionResult[]) => void;
  onLoseHeart: () => void;
  onRestoreHeart: () => void;
  onExit: () => void;
  onNextLesson?: (lessonId: string) => void;
  onRecordQuestions?: (results: QuestionResult[]) => void;
  /** Guest demo: replaces the next-lesson block on the completion screen. */
  resultFooter?: React.ReactNode;
}

type Phase = 'concept' | 'quiz' | 'result';

type ContentProgress = { requiredTotal: number; requiredDone: number; total: number; done: number };

// Shake animation for wrong answers
const shakeVariants = {
  shake: {
    x: [0, -8, 8, -6, 6, -3, 3, 0],
    transition: { duration: 0.5 },
  },
};

// Pop animation for correct answers
const popVariants = {
  pop: {
    scale: [1, 1.06, 1],
    transition: { duration: 0.35, ease: 'easeOut' },
  },
};

export function LessonView({ lesson, hearts, completedLessons, nextLesson, onComplete, onLoseHeart, onRestoreHeart, onExit, onNextLesson, onRecordQuestions, resultFooter }: LessonViewProps) {
  // Skip questions that have empty options arrays (except drag-drop which uses dragDropData)
  const validQuestions = (lesson.questions || []).filter(q => {
    if (q.type === 'drag-drop' || q.type === 'matching') return true;
    if (!q.options || q.options.length === 0) {
      console.warn(`[LessonView] Skipping question "${q.id}" in lesson "${lesson.id}" — empty options array`);
      return false;
    }
    return true;
  });

  const hasQuestions = validQuestions.length > 0;
  const hasConcepts = lesson.conceptText && lesson.conceptText.length > 0;

  // ── Personalization: adapt concept copy to user's level + position ──
  const { preferredSport, experienceMode } = usePreferredSport();
  const { playerProfile } = usePlayerProfiles();
  const { getLevel: getSportSkillLevel } = useSportSkillLevel();
  const lessonSport = lesson.id.startsWith('baseball-') ? 'baseball'
    : lesson.id.startsWith('basketball-') ? 'basketball'
    : lesson.id.startsWith('soccer-') ? 'soccer'
    : 'football';
  const lessonSportLevel = getSportSkillLevel(lessonSport);
  const personalizationCtx = useMemo(() => ({
    experienceMode,
    sport: preferredSport,
    position: playerProfile?.position ?? null,
    lessonSport,
    lessonSportLevel,
  }), [experienceMode, preferredSport, playerProfile?.position, lessonSport, lessonSportLevel]);

  // personalizedConceptText is computed after conceptIndex is declared (see below).

  const showPersonalizationBadge = isPersonalizationActive(personalizationCtx, lesson.conceptText?.length || 0);
  const personalizationBadge = showPersonalizationBadge ? (
    <span
      className="inline-flex items-center gap-1 font-display font-bold text-[10px] px-2 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary"
      title="Lesson copy adapted to your experience level and position"
    >
      ✨ {toneLabel(personalizationCtx)}
    </span>
  ) : null;



  // Restore partial progress from localStorage
  const { user: authUser } = useAuth();
  const savedKey = lessonProgressKey(authUser?.id, lesson.id);
  const saved = (() => {
    try {
      const raw = localStorage.getItem(savedKey);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && p.lessonId === lesson.id) return p;
      }
    } catch {}
    return null;
  })();

  const [phase, setPhase] = useState<Phase>(
    saved?.phase === 'quiz' && hasQuestions ? 'quiz'
    : hasConcepts ? 'concept'
    : hasQuestions ? 'quiz'
    : 'result'
  );
  const [contentProgress, setContentProgress] = useState<ContentProgress>({ requiredTotal: 0, requiredDone: 0, total: 0, done: 0 });
  const [conceptIndex, setConceptIndex] = useState(saved?.phase === 'quiz' ? (lesson.conceptText?.length || 0) - 1 : 0);

  const personalizedConceptText = useMemo(() => {
    const raw = lesson.conceptText?.[conceptIndex] ?? '';
    return personalizeConcept(raw, personalizationCtx, conceptIndex, lesson.conceptText?.length || 0, lesson.id);
  }, [lesson.id, lesson.conceptText, conceptIndex, personalizationCtx]);

  const [questionIndex, setQuestionIndex] = useState(saved?.questionIndex ?? 0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [correctCount, setCorrectCount] = useState(saved?.correctCount ?? 0);
  const [slideDir, setSlideDir] = useState(1);
  const [showCelebration, setShowCelebration] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [heartsOut, setHeartsOut] = useState(false);

  // Track individual question results
  const questionResults = useRef<QuestionResult[]>(saved?.questionResults ?? []);

  // Save partial progress to localStorage on every question advance
  useEffect(() => {
    if (phase === 'result') {
      // Clear saved progress on completion
      localStorage.removeItem(savedKey);
      return;
    }
    if (phase === 'quiz') {
      const data = {
        lessonId: lesson.id,
        phase,
        questionIndex,
        correctCount,
        questionResults: questionResults.current,
      };
      localStorage.setItem(savedKey, JSON.stringify(data));
    }
  }, [phase, questionIndex, correctCount, lesson.id, savedKey]);

  // Show hearts depleted overlay when hearts reach 0
  useEffect(() => {
    if (hearts <= 0 && phase === 'quiz' && !heartsOut) {
      setHeartsOut(true);
      // Save progress so user can resume later
      const data = {
        lessonId: lesson.id,
        questionIndex,
        correctCount,
        phase: 'quiz',
        questionResults: questionResults.current,
      };
      localStorage.setItem(savedKey, JSON.stringify(data));
    }
  }, [hearts, phase, heartsOut, lesson.id, questionIndex, correctCount, savedKey]);


  const isLastConcept = conceptIndex >= (lesson.conceptText?.length || 0) - 1;
  const requiredContentPending = Math.max(0, contentProgress.requiredTotal - contentProgress.requiredDone);
  const totalSteps = (lesson.conceptText?.length || 0) + validQuestions.length;
  const currentStep = phase === 'concept'
    ? conceptIndex
    : phase === 'quiz'
      ? (lesson.conceptText?.length || 0) + questionIndex
      : totalSteps;
  const progressPct = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 100;

  const currentQuestion = validQuestions[questionIndex];

  // Shuffle options per question (stable for the same questionIndex)
  const shuffled = useMemo(() => {
    if (!currentQuestion || !currentQuestion.options?.length) return null;
    // Don't shuffle drag-drop or true-false (they have their own UI)
    if (currentQuestion.type === 'drag-drop' || currentQuestion.type === 'true-false') return null;
    return shuffleOptionsWithCorrect(currentQuestion.options, currentQuestion.correctIndex);
  }, [questionIndex, currentQuestion?.id]);

  const displayOptions = shuffled?.shuffledOptions ?? currentQuestion?.options ?? [];
  const displayCorrectIndex = shuffled?.shuffledCorrectIndex ?? currentQuestion?.correctIndex ?? 0;

  const handleNextConcept = useCallback(() => {
    setSlideDir(1);
    if (conceptIndex < (lesson.conceptText?.length || 0) - 1) {
      setConceptIndex(i => i + 1);
    } else {
      setPhase(validQuestions.length > 0 ? 'quiz' : 'result');
    }
  }, [conceptIndex, lesson.conceptText?.length, validQuestions.length]);

  const recordResult = useCallback((questionId: string, type: string, correct: boolean) => {
    questionResults.current.push({
      questionId,
      lessonId: lesson.id,
      type,
      correct,
      timestamp: new Date().toISOString(),
    });
  }, [lesson.id]);

  const handleAnswer = useCallback((index: number) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    const correct = index === displayCorrectIndex;
    setIsCorrect(correct);
    recordResult(currentQuestion.id, currentQuestion.type, correct);
    if (correct) {
      setCorrectCount(c => c + 1);
    } else {
      onLoseHeart();
    }
  }, [selectedAnswer, currentQuestion, displayCorrectIndex, onLoseHeart, recordResult]);

  const handleNextQuestion = useCallback(() => {
    setSlideDir(1);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setHintUsed(false);
    setShowHint(false);
    if (questionIndex < validQuestions.length - 1) {
      setQuestionIndex(i => i + 1);
    } else {
      setPhase('result');
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2500);
      onComplete(correctCount, validQuestions.length, lesson.xpReward, questionResults.current);
    }
  }, [questionIndex, validQuestions.length, lesson, correctCount, onComplete]);

  const handleFinishResult = useCallback(() => {
    onExit();
  }, [onExit]);

  const handleUseHint = useCallback(() => {
    if (hintUsed || selectedAnswer !== null || hearts <= 0) return;
    setHintUsed(true);
    setShowHint(true);
    onLoseHeart();
  }, [hintUsed, selectedAnswer, hearts, onLoseHeart]);

  const isPerfect = correctCount === validQuestions.length;
  const gemsEarned = 5 + (isPerfect ? 5 : 0);

  // Generate auto-hint if none provided: eliminate one wrong answer
  const getHintText = () => {
    if (currentQuestion?.hint) return currentQuestion.hint;
    // Fallback: tell them which answer is NOT correct
    const wrongIndices = currentQuestion?.options
      .map((_, i) => i)
      .filter(i => i !== currentQuestion.correctIndex) ?? [];
    if (wrongIndices.length > 1) {
      const eliminate = wrongIndices[Math.floor(Math.random() * wrongIndices.length)];
      return `💡 Hint: "${currentQuestion.options[eliminate]}" is not the right answer.`;
    }
    return '💡 Think carefully about the key concept from the lesson.';
  };

  // Hint button component for quiz phase
  const HintButton = () => {
    if (!currentQuestion || selectedAnswer !== null) return null;
    if (hintUsed && showHint) return null; // hint already showing

    return (
      <motion.button
        onClick={handleUseHint}
        disabled={hintUsed || hearts <= 0}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`flex items-center gap-1.5 text-xs font-display font-bold px-3 py-1.5 rounded-full transition-all ${
          hintUsed
            ? 'bg-muted text-muted-foreground cursor-not-allowed'
            : hearts <= 0
              ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
              : 'bg-accent/15 text-accent hover:bg-accent/25'
        }`}
      >
        <Lightbulb className="w-3.5 h-3.5" />
        {hintUsed ? 'Hint Used' : hearts <= 0 ? 'No Hearts' : 'Use Hint (−1 ❤️)'}
      </motion.button>
    );
  };

  // Hint display
  const HintDisplay = () => {
    if (!showHint) return null;
    return (
      <motion.div
        initial={{ opacity: 0, y: -10, height: 0 }}
        animate={{ opacity: 1, y: 0, height: 'auto' }}
        className="bg-accent/10 border border-accent/20 rounded-xl p-3 overflow-hidden"
      >
        <p className="text-sm font-body text-accent-foreground leading-relaxed">
          {getHintText()}
        </p>
      </motion.div>
    );
  };

  // Next question / results button
  const NextButton = () => (
    <Button
      onClick={handleNextQuestion}
      className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
    >
      {questionIndex < validQuestions.length - 1 ? (
        <>Next Question <ArrowRight className="ml-2 w-5 h-5" /></>
      ) : (
        <>See Results 🎉</>
      )}
    </Button>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Hearts Depleted Overlay */}
      {heartsOut && (
        <HeartsDepletedOverlay
          completedLessons={completedLessons}
          onPracticeComplete={() => {
            onRestoreHeart();
            setHeartsOut(false);
          }}
          onExit={onExit}
        />
      )}
      <CelebrationOverlay
        show={showCelebration}
        xp={lesson.xpReward}
        gems={gemsEarned}
        isPerfect={isPerfect}
      />
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button onClick={onExit} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-6 h-6" />
          </button>
          <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-success rounded-full"
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <HeartsDisplay hearts={hearts} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 max-w-lg mx-auto w-full">
        <AnimatePresence mode="wait">
          {phase === 'concept' && (
            <motion.div
              key={`concept-${conceptIndex}`}
              initial={{ x: slideDir * 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full space-y-6"
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                  <span className="inline-block bg-primary/10 text-primary font-display font-bold text-xs px-3 py-1 rounded-full">
                    LEARN
                  </span>
                  {(() => {
                    const sport = lesson.id.startsWith('baseball-') ? 'baseball'
                      : lesson.id.startsWith('basketball-') ? 'basketball'
                      : lesson.id.startsWith('soccer-') ? 'soccer'
                      : 'football';
                    const cfg = { football: { emoji: '🏈', label: 'Football', cls: 'text-[#C9A84C] bg-[#C9A84C]/10 border-[#C9A84C]/30' },
                      soccer: { emoji: '⚽', label: 'Soccer', cls: 'text-[#2D9B4F] bg-[#2D9B4F]/10 border-[#2D9B4F]/30' },
                      basketball: { emoji: '🏀', label: 'Basketball', cls: 'text-[#E8651A] bg-[#E8651A]/10 border-[#E8651A]/30' },
                      baseball: { emoji: '⚾', label: 'Baseball', cls: 'text-[#C8443A] bg-[#C8443A]/10 border-[#C8443A]/30' } }[sport];
                    return (
                      <span className={`inline-flex items-center gap-1 font-display font-bold text-xs px-3 py-1 rounded-full border ${cfg.cls}`}>
                        <span>{cfg.emoji}</span>{cfg.label}
                      </span>
                    );
                  })()}
                  {personalizationBadge}
                </div>
                <h2 className="font-display font-extrabold text-2xl mb-2">{lesson.title}</h2>
              </div>

              <div className="bg-card rounded-2xl border-2 border-border p-6 shadow-card">
                <p className="font-body text-base leading-relaxed text-foreground">
                  <GlossaryText text={personalizedConceptText} />
                </p>
              </div>


              {(() => {
                const lessonSport = lesson.id.startsWith('baseball-') ? 'baseball'
                  : lesson.id.startsWith('basketball-') ? 'basketball'
                  : lesson.id.startsWith('soccer-') ? 'soccer'
                  : 'football';
                // Same term matcher that highlights glossary words above, run over
                // the FULL lesson text: description, every concept section
                // (personalized the same way it is displayed), and every quiz
                // question, option, explanation and hint.
                const sections = lesson.conceptText ?? [];
                const parts: string[] = [
                  lesson.description ?? '',
                  ...sections.map((raw, i) =>
                    personalizeConcept(raw, personalizationCtx, i, sections.length, lesson.id)),
                ];
                for (const q of lesson.questions ?? []) {
                  if (q.question) parts.push(q.question);
                  if (Array.isArray(q.options)) parts.push(...q.options.filter(Boolean));
                  if (q.explanation) parts.push(q.explanation);
                  if (q.hint) parts.push(q.hint);
                }
                const contextText = parts.join(' \n ');
                return (
                  <GlossaryPanel sport={lessonSport} contextText={contextText} />
                );
              })()}

              {isLastConcept && (
                <LessonContentSection lessonId={lesson.id} onProgress={setContentProgress} />
              )}

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{conceptIndex + 1} of {(lesson.conceptText?.length || 0)}</span>
                {isLastConcept && contentProgress.total > 0 && (
                  <span>{contentProgress.done} of {contentProgress.total} content items done</span>
                )}
              </div>

              {isLastConcept && requiredContentPending > 0 && (
                <p className="text-[11px] text-center text-muted-foreground">
                  Finish {requiredContentPending} required item{requiredContentPending === 1 ? '' : 's'} above to unlock the quiz.
                </p>
              )}

              <Button
                disabled={isLastConcept && requiredContentPending > 0}
                onClick={handleNextConcept}
                className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
              >
                {conceptIndex < (lesson.conceptText?.length || 0) - 1 ? (
                  <>Continue <ArrowRight className="ml-2 w-5 h-5" /></>
                ) : (
                  <>Start Quiz</>
                )}
              </Button>
            </motion.div>
          )}

          {phase === 'quiz' && currentQuestion && (
            <motion.div
              key={`quiz-${questionIndex}`}
              initial={{ x: slideDir * 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full space-y-5"
            >
              {/* Drag-Drop question type */}
              {currentQuestion.type === 'drag-drop' && currentQuestion.dragDropData ? (
                <>
                  <div className="flex justify-end">
                    <HintButton />
                  </div>
                  <HintDisplay />
                  <DragDropQuestion
                    question={currentQuestion.question}
                    data={currentQuestion.dragDropData}
                    onAnswer={(correct) => {
                      setIsCorrect(correct);
                      setSelectedAnswer(correct ? 0 : -1);
                      recordResult(currentQuestion.id, currentQuestion.type, correct);
                      if (correct) {
                        setCorrectCount(c => c + 1);
                      } else {
                        onLoseHeart();
                      }
                    }}
                    answered={selectedAnswer !== null}
                  />
                  {selectedAnswer !== null && (
                    <>
                      <CoachFeedback
                        type={isCorrect ? 'correct' : 'incorrect'}
                        explanation={currentQuestion.explanation}
                      />
                      <NextButton />
                    </>
                  )}
                </>
              ) : currentQuestion.type === 'true-false' ? (
                <>
                  <div className="flex justify-end">
                    <HintButton />
                  </div>
                  <HintDisplay />
                  <TrueFalseQuestion
                    question={currentQuestion.question}
                    correctAnswer={currentQuestion.correctAnswer ?? false}
                    explanation={currentQuestion.explanation}
                    onAnswer={(correct) => {
                      setIsCorrect(correct);
                      setSelectedAnswer(correct ? 0 : -1);
                      recordResult(currentQuestion.id, currentQuestion.type, correct);
                      if (correct) {
                        setCorrectCount(c => c + 1);
                      } else {
                        onLoseHeart();
                      }
                    }}
                    answered={selectedAnswer !== null}
                  />
                  {selectedAnswer !== null && (
                    <>
                      <CoachFeedback
                        type={isCorrect ? 'correct' : 'incorrect'}
                        explanation={currentQuestion.explanation}
                      />
                      <NextButton />
                    </>
                  )}
                </>
              ) : currentQuestion.type === 'scenario' ? (
                <>
                  <div className="flex justify-end">
                    <HintButton />
                  </div>
                  <HintDisplay />
                  <ScenarioQuestion
                    question={currentQuestion.question}
                    options={displayOptions}
                    correctIndex={displayCorrectIndex}
                    explanation={currentQuestion.explanation}
                    onAnswer={(index) => handleAnswer(index)}
                    selectedAnswer={selectedAnswer}
                    isCorrect={isCorrect}
                  />
                  {selectedAnswer !== null && (
                    <>
                      <CoachFeedback
                        type={isCorrect ? 'correct' : 'incorrect'}
                        explanation={currentQuestion.explanation}
                      />
                      <NextButton />
                    </>
                  )}
                </>
              ) : currentQuestion.type === 'situational' && currentQuestion.situationContext ? (
                <>
                  <div className="flex justify-end">
                    <HintButton />
                  </div>
                  <HintDisplay />
                  <SituationalPlayCall
                    question={currentQuestion.question}
                    situationContext={currentQuestion.situationContext}
                    options={displayOptions}
                    correctIndex={displayCorrectIndex}
                    explanation={currentQuestion.explanation}
                    onAnswer={(index) => handleAnswer(index)}
                    selectedAnswer={selectedAnswer}
                    isCorrect={isCorrect}
                  />
                  {selectedAnswer !== null && (
                    <>
                      <CoachFeedback
                        type={isCorrect ? 'correct' : 'incorrect'}
                        explanation={currentQuestion.explanation}
                      />
                      <NextButton />
                    </>
                  )}
                </>
              ) : currentQuestion.type === 'matching' && currentQuestion.matchingPairs ? (
                <>
                  <div className="flex justify-end">
                    <HintButton />
                  </div>
                  <HintDisplay />
                  <MatchingPairsQuestion
                    question={currentQuestion.question}
                    pairs={currentQuestion.matchingPairs}
                    onAnswer={(correct) => {
                      setIsCorrect(correct);
                      setSelectedAnswer(correct ? 0 : -1);
                      recordResult(currentQuestion.id, currentQuestion.type, correct);
                      if (correct) {
                        setCorrectCount(c => c + 1);
                      } else {
                        onLoseHeart();
                      }
                    }}
                    answered={selectedAnswer !== null}
                  />
                  {selectedAnswer !== null && (
                    <>
                      <CoachFeedback
                        type={isCorrect ? 'correct' : 'incorrect'}
                        explanation={currentQuestion.explanation}
                      />
                      <NextButton />
                    </>
                  )}
                </>
              ) : currentQuestion.type === 'diagram-identify' && currentQuestion.diagram ? (
                <>
                  <div className="flex justify-end">
                    <HintButton />
                  </div>
                  <HintDisplay />
                  <DiagramIdentifyQuestion
                    question={currentQuestion.question}
                    diagram={currentQuestion.diagram}
                    options={displayOptions}
                    correctIndex={displayCorrectIndex}
                    onAnswer={(index) => handleAnswer(index)}
                    selectedAnswer={selectedAnswer}
                    isCorrect={isCorrect}
                  />
                  {selectedAnswer !== null && (
                    <>
                      <CoachFeedback
                        type={isCorrect ? 'correct' : 'incorrect'}
                        explanation={currentQuestion.explanation}
                      />
                      <NextButton />
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className={`inline-block font-display font-bold text-xs px-3 py-1 rounded-full ${
                      currentQuestion.type === 'fix-mistake'
                        ? 'bg-destructive/10 text-destructive'
                        : currentQuestion.type === 'identify'
                          ? 'bg-accent/20 text-accent-foreground'
                          : 'bg-primary/10 text-primary'
                    }`}>
                      {currentQuestion.type === 'fix-mistake' ? '🚩 FIX THE MISTAKE' :
                       currentQuestion.type === 'identify' ? '🔍 IDENTIFY THE PLAY' :
                       '❓ QUIZ'}
                    </span>
                    <HintButton />
                  </div>

                  <HintDisplay />

                  {currentQuestion.diagram && (
                    <PlayDiagram diagram={currentQuestion.diagram} />
                  )}

                  <p className="font-body font-semibold text-lg text-center leading-snug">
                    {currentQuestion.question}
                  </p>

                  <div className="space-y-2.5">
                    {displayOptions.map((option, i) => {
                      const isSelected = i === selectedAnswer;
                      const isCorrectOption = i === displayCorrectIndex;
                      const answered = selectedAnswer !== null;

                      let variant = 'bg-card border-border hover:border-primary/50';
                      if (answered) {
                        if (isCorrectOption) {
                          variant = 'bg-success/10 border-success ring-2 ring-success/30';
                        } else if (isSelected && !isCorrect) {
                          variant = 'bg-destructive/10 border-destructive ring-2 ring-destructive/30';
                        } else {
                          variant = 'bg-muted/50 border-border opacity-50';
                        }
                      }

                      const animateState = answered
                        ? isSelected && isCorrect
                          ? 'pop'
                          : isSelected && !isCorrect
                            ? 'shake'
                            : undefined
                        : undefined;

                      return (
                        <motion.button
                          key={i}
                          onClick={() => handleAnswer(i)}
                          disabled={answered}
                          whileTap={!answered ? { scale: 0.98 } : {}}
                          variants={{ ...shakeVariants, ...popVariants }}
                          animate={animateState}
                          className={`w-full p-4 rounded-xl border-2 text-left font-body font-medium transition-colors ${variant}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-display font-bold text-sm text-muted-foreground">
                              {String.fromCharCode(65 + i)}.
                            </span>
                            <span className="flex-1">{option}</span>
                            {answered && isCorrectOption && (
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }}
                                className="text-success text-lg"
                              >
                                ✅
                              </motion.span>
                            )}
                            {answered && isSelected && !isCorrect && (
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }}
                                className="text-destructive text-lg"
                              >
                                ❌
                              </motion.span>
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>

                  {selectedAnswer !== null && (
                    <>
                      <CoachFeedback
                        type={isCorrect ? 'correct' : 'incorrect'}
                        explanation={currentQuestion.explanation}
                      />
                      <NextButton />
                    </>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* Fallback: quiz phase but no valid questions */}
          {phase === 'quiz' && !currentQuestion && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="w-full text-center space-y-6 py-8"
            >
              <div className="text-5xl">📚</div>
              <h3 className="font-display font-extrabold text-xl">Content Coming Soon!</h3>
              <p className="text-sm text-muted-foreground font-body max-w-xs mx-auto">
                We're still building the quiz for this lesson. Check back soon!
              </p>
              <Button
                onClick={onExit}
                className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
              >
                <Home className="mr-2 w-5 h-5" />
                Back to Lessons
              </Button>
            </motion.div>
          )}

          {phase === 'result' && (
            <motion.div
              key="result"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative z-10 w-full text-center space-y-6"
            >
              <div className="text-6xl mb-2">
                {correctCount === validQuestions.length ? '🏆' :
                 correctCount >= validQuestions.length * 0.7 ? '🎉' : '💪'}
              </div>
              <h2 className="font-display font-extrabold text-3xl">
                {correctCount === validQuestions.length ? 'Perfect Game!' :
                 correctCount >= validQuestions.length * 0.7 ? 'Great Drive!' : 'Keep Studying!'}
              </h2>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-card rounded-xl border-2 border-border p-4">
                  <p className="display-number text-2xl text-primary">{correctCount}/{validQuestions.length}</p>
                  <p className="text-xs text-muted-foreground">Correct</p>
                </div>
                <div className="bg-card rounded-xl border-2 border-border p-4">
                  <p className="display-number text-2xl text-accent">+{lesson.xpReward}</p>
                  <p className="text-xs text-muted-foreground">XP Earned</p>
                </div>
                <div className="bg-card rounded-xl border-2 border-border p-4">
                  <p className="display-number text-2xl text-primary">+{gemsEarned}</p>
                  <p className="text-xs text-muted-foreground">💎 Gems</p>
                </div>
              </div>

              {/* Weak area callout if user got questions wrong */}
              {correctCount < validQuestions.length && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-accent/10 rounded-xl border-2 border-accent/30 p-4 text-left"
                >
                  <p className="font-display font-bold text-sm text-accent-foreground">📊 Areas to improve</p>
                  <div className="mt-2 space-y-1">
                    {questionResults.current
                      .filter(r => !r.correct)
                      .map(r => (
                        <p key={r.questionId} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-destructive flex-shrink-0" />
                          {r.type === 'fix-mistake' ? 'Fix the Mistake' :
                           r.type === 'identify' ? 'Play Identification' :
                           r.type === 'drag-drop' ? 'Formation Building' :
                           r.type === 'diagram-identify' ? 'Diagram Reading' :
                           'Multiple Choice'} — missed
                        </p>
                      ))
                    }
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 font-body">
                    We'll prioritize these in your Smart Review sessions.
                  </p>
                </motion.div>
              )}

              {/* Next Lesson Prompt */}
              {resultFooter ? resultFooter : nextLesson && onNextLesson ? (
                <div className="space-y-3">
                  <div className="bg-muted/50 rounded-xl border-2 border-border p-4">
                    <p className="text-xs text-muted-foreground font-display font-bold uppercase mb-1">Up Next</p>
                    <p className="font-display font-bold text-base">{nextLesson.title}</p>
                    <p className="text-xs text-muted-foreground font-body mt-0.5">{nextLesson.description}</p>
                    <span className="text-xs font-display font-bold text-accent mt-1 inline-block">+{nextLesson.xpReward} XP</span>
                  </div>
                  <Button
                    onClick={() => onNextLesson(nextLesson.id)}
                    className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
                  >
                    Next Lesson <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <button
                    onClick={handleFinishResult}
                    className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-body py-2"
                  >
                    <Home className="w-4 h-4" />
                    Back to Home
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-success/10 rounded-xl border-2 border-success/30 p-4">
                    <p className="font-display font-bold text-sm text-success">🎓 You've completed all available lessons!</p>
                    <p className="text-xs text-muted-foreground font-body mt-1">Check back for new content or review past lessons.</p>
                  </div>
                  <Button
                    onClick={handleFinishResult}
                    className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
                  >
                    <Home className="mr-2 w-5 h-5" />
                    Back to Home
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
